/**
 * UnmatchedTagsPanel — tab body for the Inventory detail page's
 * "Unmatched EPCs" tab. EPCs read by the latest scan that have no
 * mapping in md_rfid, so they aren't counted in inventory / refill.
 *
 * The list is passed in by the parent (already loaded from
 * /api/boxes/{scu}/inventory) — no fetch here. The parent decides
 * whether to render this panel at all based on the current tab.
 *
 * Server caps `unmatched` at 500 EPCs (see inventory_db._UNMATCHED_CAP);
 * `unmatchedCount` carries the true total, and this component flags the
 * cap when it kicks in.
 */
import React from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";

interface UnmatchedTagsPanelProps {
  scu: string;
  unmatched: string[];
  unmatchedCount: number;
}

export const UnmatchedTagsPanel: React.FC<UnmatchedTagsPanelProps> = ({
  unmatched,
  unmatchedCount,
}) => {
  const { can } = useAuth();
  const canMatch = can("tags.manage");
  const capped = unmatchedCount > unmatched.length;

  return (
    <section className="bg-white border border-secondary-200 rounded-md p-4">
      <div className="text-sm text-secondary-700 mb-3">
        <strong className="text-secondary-900">{unmatchedCount}</strong> EPC
        {unmatchedCount === 1 ? "" : "s"} in this box's latest scan
        {unmatchedCount > 0 ? " have no catalog mapping" : " were unmatched"}.
        {unmatchedCount > 0 && (
          <span className="text-xs text-secondary-500 ml-1">
            (excluded from inventory + refill totals)
          </span>
        )}
      </div>

      {unmatchedCount > 0 && (
        <>
          <ul className="max-h-[32rem] overflow-auto border border-secondary-100 rounded divide-y divide-secondary-100">
            {unmatched.map((epc) => (
              <li
                key={epc}
                className="px-2 py-1 text-xs font-mono text-secondary-800 hover:bg-secondary-50"
              >
                {epc}
              </li>
            ))}
          </ul>
          {capped && (
            <div className="text-xs text-secondary-500 mt-2 italic">
              Showing the first {unmatched.length} of {unmatchedCount}. The
              remaining {unmatchedCount - unmatched.length} aren't listed
              (server cap).
            </div>
          )}
          {canMatch && (
            <div className="mt-3 text-xs">
              <Link
                to="/tags/match"
                className="text-primary-700 hover:text-primary-900 hover:underline"
              >
                → Match these tags to a product
              </Link>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default UnmatchedTagsPanel;
